import { holdoutRulesDefaults } from './defaults';
import type { HoldoutDef, HoldoutRulesDef } from './types';

/*
 * The holdout preview (holdout.html §5 Admin): the stacked wave chart per squad size against the live caps, the totals, the kill bound
 * and the minimum duration, from a port of shared-cs `Raids/HoldoutRules` (SquadScale, Scaled, MaxKills, MinDurationS) and the
 * seed-independent parts of `WavePlanner.PlanHoldout` (each wave's entries, its size, and its queue's release end), so the admin and the
 * server agree. The shuffle and the boss entry are seeded, but the sizes and release ends don't depend on them: bodies are dealt to the
 * entry with the fewest so far, so the busiest entry ends up with ceil(size / entries).
 */

/** `HoldoutRules.EntranceEntry`: a wave with no open entry comes in at the building entrance. */
export const ENTRANCE_ENTRY = 'entrance';
/** `HoldoutRules.MaxWaves`. */
export const MAX_WAVES = 12;
/** `HoldoutRules.DefaultSquadScale` (the page's numbers; content opts in). */
export const DEFAULT_SQUAD_SCALE = [0.6, 0.8, 1, 1.15];

/** `HoldoutRules.SquadScale`: 0 or 1 = solo; beyond the list, its last value; an empty list = 1; a non-positive factor = 1. */
export function squadScaleFor(h: HoldoutDef | null | undefined, squadSize: number): number {
  const list = h?.squadScale ?? [];
  if (!list.length) return 1;
  const i = Math.max(0, Math.min(list.length - 1, Math.max(1, squadSize) - 1));
  return list[i]! > 0 ? list[i]! : 1;
}

/** `HoldoutRules.Scaled`: half up with a 1e-4 nudge (10 × 1.15 = 12), never below 1 when the count is. */
export function scaled(count: number, scale: number): number {
  if (count <= 0) return 0;
  return Math.max(1, Math.floor(count * Math.fround(scale) + 0.5 + 1e-4));
}

export type WaveBar = {
  wave: number;
  /** zombieId → bodies (scaled; the boss's own mix count unscaled). */
  mix: { zombieId: string; count: number }[];
  /** `HoldoutWavePlan.Size`. */
  total: number;
  boss: string;
  entries: string[];
  /** `HoldoutWavePlan.ReleaseEndS`: the release time of the queue's last body. */
  releaseS: number;
  crate: string;
};

export type HoldoutPreview = {
  squadSize: number;
  scale: number;
  waves: WaveBar[];
  totalZombies: number;
  /** `HoldoutRules.MaxKills(plan, every wave)`: the holdout term of the squad kill bound (room zombies come on top). */
  killBound: number;
  /** `HoldoutRules.MinDurationS(plan, every wave)`: setup and breathers can be skipped, so they don't count. */
  minDurationS: number;
};

export function previewHoldout(h: HoldoutDef, squadSize: number): HoldoutPreview {
  const scale = squadScaleFor(h, squadSize);
  const waves: WaveBar[] = (h.waves ?? []).map((raw, i) => {
    const def = raw ?? {};
    const number = i + 1;
    const interval = Math.max(0, def.spawnIntervalS ?? 1);
    const entries: string[] = [];
    for (const e of def.entries ?? []) {
      if (!e || entries.includes(e)) continue;
      const entry = (h.entries ?? []).find((x) => x != null && x.id === e);
      if (entry && (entry.from ?? 1) > number) continue;
      entries.push(e);
    }
    if (!entries.length) entries.push(ENTRANCE_ENTRY);
    const boss = def.boss ?? '';
    let bossPlaced = false;
    const mix: { zombieId: string; count: number }[] = [];
    for (const m of def.mix ?? []) {
      if (!m || !m.zombieId || (m.count ?? 0) <= 0) continue;
      let n = scaled(m.count ?? 0, scale);
      if (m.zombieId === boss && !bossPlaced) {
        bossPlaced = true;
        n = (m.count ?? 0) - 1 + 1; // the boss is one body, the rest of its mix count is ordinary and unscaled
      }
      const at = mix.find((x) => x.zombieId === m.zombieId);
      if (at) at.count += n;
      else mix.push({ zombieId: m.zombieId, count: n });
    }
    const total = mix.reduce((a, m) => a + m.count, 0);
    const busiest = Math.ceil(total / entries.length);
    return {
      wave: number,
      mix,
      total,
      boss: bossPlaced ? boss : '',
      entries,
      releaseS: total === 0 ? 0 : Math.fround((busiest - 1) * Math.fround(interval)),
      crate: def.crate ? `${def.crate.lootTableId ?? ''} ×${def.crate.rolls ?? 0}` : '',
    };
  });
  const totalZombies = waves.reduce((a, w) => a + w.total, 0);
  let minDurationS = 0;
  for (const w of waves) minDurationS = Math.fround(minDurationS + w.releaseS);
  return {
    squadSize,
    scale,
    waves,
    totalZombies,
    killBound: totalZombies,
    minDurationS: Math.round(minDurationS * 100) / 100,
  };
}

export const liveCaps = (r: HoldoutRulesDef | null | undefined) => {
  const d = holdoutRulesDefaults().liveCap;
  const c = r?.liveCap ?? {};
  return { low: c.low ?? d.low ?? 18, mid: c.mid ?? d.mid ?? 28, high: c.high ?? d.high ?? 40 };
};
